import { useState, useEffect, useCallback } from 'react';
import { Button, ActionIcon, TextInput, Text, Paper } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPlus, IconTrash, IconEdit } from '@tabler/icons-react';
import apiService from '../services/api';
import { useTranslation } from '../context/TranslationContext';
import LanguageTabs from './LanguageTabs';
import './AdminProducts.css';
import './ShippingListSection.css';

export default function HolidayManagementSection() {
  const { t } = useTranslation();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ start_date: '', end_date: '' });
  const [translationsForm, setTranslationsForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const today = new Date().toISOString().split('T')[0];

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setHolidays(await apiService.getAdminHolidays());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({ start_date: '', end_date: '' });
    setTranslationsForm({});
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  const startAdd = () => {
    resetForm();
    setShowForm(true);
  };

  const startEdit = (holiday) => {
    setForm({
      start_date: holiday.start_date,
      end_date: holiday.end_date,
    });
    // Build translations form from the holiday.translations array
    const transMap = {};
    for (const tr of (holiday.translations || [])) {
      transMap[tr.language_code] = tr.message || '';
    }
    setTranslationsForm(transMap);
    setEditingId(holiday.id);
    setShowForm(true);
    setError('');
  };

  const handleChange = (field) => (e) => {
    const value = e.currentTarget.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleTranslationChange = (langCode, value) => {
    setTranslationsForm((prev) => ({ ...prev, [langCode]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date) {
      setError('Start date and end date are required.');
      return;
    }
    if (form.end_date < form.start_date) {
      setError(t('holidayStartEndError', 'End date must be on or after the start date.'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const translations = Object.entries(translationsForm)
        .filter(([, v]) => v !== undefined)
        .map(([language_code, message]) => ({ language_code, message: message || null }));

      const payload = {
        start_date: form.start_date,
        end_date: form.end_date,
        translations,
      };
      if (editingId) {
        await apiService.updateAdminHoliday(editingId, payload);
      } else {
        await apiService.createAdminHoliday(payload);
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => {
    modals.openConfirmModal({
      title: t('deleteHoliday', 'Delete Holiday'),
      centered: true,
      children: <Text size="sm">{t('confirmDeleteHoliday', 'Delete this holiday period?')}</Text>,
      labels: { confirm: t('delete', 'Delete'), cancel: t('cancel', 'Cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await apiService.deleteAdminHoliday(id);
          await load();
        } catch (err) {
          console.error(err);
        }
      },
    });
  };

  const getMessage = (holiday, langCode) => {
    const tr = (holiday.translations || []).find(t => t.language_code === langCode);
    return tr?.message || null;
  };

  return (
    <div className="settings-section">
      <div className="settings-section-header">
        <h2 className="settings-section-title">{t('holidayManagement', 'Holiday Management')}</h2>
        <p className="settings-section-description">{t('holidayManagementDesc', 'Configure holiday periods when the shop is closed.')}</p>
      </div>

      <Button
        onClick={startAdd}
        color="green"
        leftSection={<IconPlus size={16} />}
        mb="md"
        disabled={showForm}
      >
        {t('addHoliday', 'Add Holiday')}
      </Button>

      {showForm && (
        <Paper withBorder p="md" radius="sm" mb="md" component="form" onSubmit={handleSave}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <TextInput
              label={t('startDate', 'Start Date')}
              type="date"
              value={form.start_date}
              onChange={handleChange('start_date')}
              required
              aria-required="true"
            />
            <div>
              <TextInput
                label={t('endDate', 'End Date')}
                type="date"
                value={form.end_date}
                onChange={handleChange('end_date')}
                min={form.start_date || today}
                required
                aria-required="true"
              />
              <Text size="xs" c="dimmed" mt={4}>
                {t('endDateInclusive', 'Holiday includes this date — we will be back the next day.')}
              </Text>
            </div>
          </div>

          <LanguageTabs>
            {({ language, langCode }) => (
              <div key={langCode}>
                <TextInput
                  label={t('messageEn', 'Message')}
                  value={translationsForm[langCode] || ''}
                  onChange={(e) => handleTranslationChange(langCode, e.currentTarget.value)}
                  mb={4}
                />
              </div>
            )}
          </LanguageTabs>

          {error && (
            <Text role="alert" c="red" size="sm" mb="sm">{error}</Text>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <Button type="submit" loading={saving} disabled={saving}>
              {editingId ? 'Save' : t('addHoliday', 'Add Holiday')}
            </Button>
            <Button variant="subtle" color="gray" onClick={resetForm} disabled={saving}>
              {t('cancel', 'Cancel')}
            </Button>
          </div>
        </Paper>
      )}

      {loading ? (
        <p className="settings-section-loading" role="status" aria-live="polite">{t('loading', 'Loading...')}</p>
      ) : holidays.length === 0 ? (
        <Text role="status" aria-live="polite" c="dimmed" size="sm" mt="md">
          {t('noHolidaysConfigured', 'No holidays configured.')}
        </Text>
      ) : (
        <div className="table-responsive" style={{ marginTop: '1rem' }}>
          <table className="admin-table">
            <caption className="sr-only">{t('holidayManagement', 'Holiday Management')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('startDate', 'Start Date')}</th>
                <th scope="col">{t('endDate', 'End Date')}</th>
                <th scope="col">{t('messageEn', 'Message (English)')}</th>
                <th scope="col" style={{ width: 100 }}>{t('actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {holidays.map((h) => (
                <tr key={h.id}>
                  <td>{h.start_date}</td>
                  <td>{h.end_date}</td>
                  <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {getMessage(h, 'en') || <Text component="span" c="dimmed" size="sm">(default)</Text>}
                  </td>
                  <td>
                    <div className="inline-actions">
                      <ActionIcon variant="subtle" color="gray" onClick={() => startEdit(h)} title={t('editHoliday', 'Edit Holiday')}>
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(h.id)} title={t('deleteHoliday', 'Delete Holiday')}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
